/**
 * Predictive Analytics Component
 * Real-time predictions and performance tracking
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../api/client';

interface WaitTimePrediction {
  currentWaitTime: number;
  predictedWaitTime: number;
  confidence: number;
  tablesAvailable: number;
  tablesOccupied: number;
  averageServiceTime: number;
  nextAvailableIn: number;
  factors: string[];
}

interface TableTurnoverPrediction {
  tableId: number;
  estimatedFreeIn: number;
  confidence: number;
  currentActivity: string;
  predictedEndTime: string;
}

interface PerformanceAnalytics {
  avgServiceTime: number;
  tableTurnoverRate: number;
  occupancyRate: number;
  avgWaitTime: number;
  peakHours: number[];
  trend: 'increasing' | 'stable' | 'decreasing';
  comparisonToYesterday: number;
  comparisonToLastWeek: number;
}

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const CardTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Value = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #2196f3;
  margin: 8px 0;
`;

const SubValue = styled.div`
  font-size: 14px;
  color: #666;
  margin: 4px 0;
`;

const Confidence = styled.div<{ confidence: number }>`
  font-size: 12px;
  color: ${props => props.confidence > 0.8 ? '#4caf50' : props.confidence > 0.6 ? '#ff9800' : '#f44336'};
  font-weight: 600;
  margin-top: 8px;
`;

const FactorsList = styled.ul`
  font-size: 12px;
  color: #666;
  margin: 8px 0 0 0;
  padding-left: 20px;
  list-style: disc;
`;

const TrendIndicator = styled.span<{ trend: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => 
    props.trend === 'increasing' ? '#4caf50' :
    props.trend === 'decreasing' ? '#f44336' :
    '#ff9800'
  };
`;

const Badge = styled.span<{ type?: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => 
    props.type === 'success' ? '#e8f5e9' :
    props.type === 'warning' ? '#fff3e0' :
    props.type === 'error' ? '#ffebee' :
    '#e3f2fd'
  };
  color: ${props =>
    props.type === 'success' ? '#4caf50' :
    props.type === 'warning' ? '#ff9800' :
    props.type === 'error' ? '#f44336' :
    '#2196f3'
  };
  margin: 4px 4px 4px 0;
`;

const PredictiveAnalytics: React.FC = () => {
  const [waitTime, setWaitTime] = useState<WaitTimePrediction | null>(null);
  const [tableTurnover, setTableTurnover] = useState<TableTurnoverPrediction[]>([]);
  const [performance, setPerformance] = useState<PerformanceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchPredictions = async () => {
    try {
      const [waitTimeRes, turnoverRes, perfRes] = await Promise.all([
        api.get('/analytics/predictions/wait-time?locationId=1'),
        api.get('/analytics/predictions/table-turnover?locationId=1'),
        api.get('/analytics/performance?locationId=1')
      ]);

      setWaitTime(waitTimeRes.data);
      setTableTurnover(turnoverRes.data);
      setPerformance(perfRes.data);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch predictions:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPredictions();

    // Refresh predictions every 30 seconds
    const interval = setInterval(fetchPredictions, 30000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Container>
        <Card>
          <CardTitle>Loading Predictive Analytics...</CardTitle>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      {/* Wait Time Prediction */}
      <Card>
        <CardTitle>⏱️ Wait Time Prediction</CardTitle>
        {waitTime ? (
          <>
            <Value>
              {waitTime.currentWaitTime === 0 ? 'No Wait' : `${waitTime.currentWaitTime} min`}
            </Value>
            {waitTime.currentWaitTime > 0 && (
              <>
                <SubValue>Next table available in {waitTime.nextAvailableIn} min</SubValue>
                <SubValue>Avg service time: {Math.round(waitTime.averageServiceTime)} min</SubValue>
                <Confidence confidence={waitTime.confidence}>
                  {Math.round(waitTime.confidence * 100)}% confidence
                </Confidence>
                <FactorsList>
                  {waitTime.factors.map((factor, i) => (
                    <li key={i}>{factor}</li>
                  ))}
                </FactorsList>
              </>
            )}
            <SubValue style={{ marginTop: '12px', fontSize: '11px', opacity: 0.7 }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </SubValue>
          </>
        ) : (
          <SubValue>No prediction available</SubValue>
        )}
      </Card>

      {/* Table Availability */}
      <Card>
        <CardTitle>📋 Table Status</CardTitle>
        {waitTime ? (
          <>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              <div>
                <Value style={{ fontSize: '24px', color: '#4caf50' }}>{waitTime.tablesAvailable}</Value>
                <SubValue>Available</SubValue>
              </div>
              <div>
                <Value style={{ fontSize: '24px', color: '#f44336' }}>{waitTime.tablesOccupied}</Value>
                <SubValue>Occupied</SubValue>
              </div>
            </div>
            {tableTurnover.length > 0 && (
              <>
                <SubValue style={{ marginTop: '16px', fontWeight: 600 }}>
                  Next to Free:
                </SubValue>
                {tableTurnover.slice(0, 3).map((table) => (
                  <div key={table.tableId} style={{ marginTop: '8px' }}>
                    <Badge type="warning">Table {table.tableId}</Badge>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      in {table.estimatedFreeIn} min
                    </span>
                  </div>
                ))}
              </>
            )}
          </>
        ) : (
          <SubValue>No data available</SubValue>
        )}
      </Card>

      {/* Performance Analytics */}
      <Card>
        <CardTitle>📊 Performance Metrics</CardTitle>
        {performance ? (
          <>
            <SubValue>
              <strong>Service Time:</strong> {performance.avgServiceTime} min
            </SubValue>
            <SubValue>
              <strong>Turnover Rate:</strong> {performance.tableTurnoverRate.toFixed(1)} tables/hour
            </SubValue>
            <SubValue>
              <strong>Occupancy:</strong> {performance.occupancyRate.toFixed(1)}%
            </SubValue>
            <SubValue style={{ marginTop: '12px' }}>
              <strong>Trend:</strong>{' '}
              <TrendIndicator trend={performance.trend}>
                {performance.trend === 'increasing' && '↗️'}
                {performance.trend === 'decreasing' && '↘️'}
                {performance.trend === 'stable' && '→'}
                {' '}{performance.trend}
              </TrendIndicator>
            </SubValue>
            <SubValue style={{ marginTop: '8px' }}>
              <strong>vs Yesterday:</strong>{' '}
              <span style={{ color: performance.comparisonToYesterday > 0 ? '#4caf50' : '#f44336' }}>
                {performance.comparisonToYesterday > 0 ? '+' : ''}{performance.comparisonToYesterday}%
              </span>
            </SubValue>
            <SubValue>
              <strong>vs Last Week:</strong>{' '}
              <span style={{ color: performance.comparisonToLastWeek > 0 ? '#4caf50' : '#f44336' }}>
                {performance.comparisonToLastWeek > 0 ? '+' : ''}{performance.comparisonToLastWeek}%
              </span>
            </SubValue>
          </>
        ) : (
          <SubValue>Collecting performance data...</SubValue>
        )}
      </Card>

      {/* Peak Hours */}
      <Card>
        <CardTitle>🔥 Peak Hours</CardTitle>
        {performance && performance.peakHours.length > 0 ? (
          <>
            <SubValue style={{ marginBottom: '12px' }}>
              Busiest times based on historical data:
            </SubValue>
            {performance.peakHours.map((hour) => (
              <Badge key={hour} type="error">
                {hour}:00 - {hour + 1}:00
              </Badge>
            ))}
          </>
        ) : (
          <SubValue>Analyzing patterns...</SubValue>
        )}
      </Card>
    </Container>
  );
};

export default PredictiveAnalytics;

